import { LogoWithText, TickIconCircleFill } from 'assets';
import { useSubmitCandidateFeedback, useToast } from 'hooks';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Five emoji options mapped evenly across the backend's 1–10 rating scale.
const RATING_OPTIONS = [
  { value: 2, emoji: '😞', label: 'Poor' },
  { value: 4, emoji: '🙁', label: 'Fair' },
  { value: 6, emoji: '😐', label: 'Okay' },
  { value: 8, emoji: '🙂', label: 'Good' },
  { value: 10, emoji: '🤩', label: 'Excellent' },
];

const AssessmentFeedback = () => {
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { id } = useParams<{ id: string }>();
  const { submitFeedback, isLoading: isSubmittingFeedback } = useSubmitCandidateFeedback(id ?? '');
  const { openToast } = useToast();
  const navigate = useNavigate();

  const handleRatingClick = (value: number) => {
    setRating(value);
  };

  const handleSubmitFeedback = () => {
    if (!rating) {
      openToast({ show: true, heading: 'Error', text: 'Please select a rating before submitting.', type: 'error', timeOut: 5000 });
      return;
    }

    submitFeedback(
      { rating, feedback: comment },
      {
        onSuccess: () => {
          setIsSubmitted(true);
          setTimeout(() => navigate('/'), 2000);
        },
      }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 w-full flex items-center justify-between px-4 h-[90px] bg-[#F5FAFF] border-b border-gray-200 z-[99]">
        <div className="flex items-center space-x-4">
          <LogoWithText className='' />
        </div>
      </nav>

      {/* Main Container */}
      <div className="mt-[90px] bg-gray-50 flex flex-col items-center px-5 py-10 relative md:px-4 md:py-6 scrollbar-hide">
        <div className="w-full max-w-[856px] flex flex-col gap-8">

          {/* Success Section */}
          <div className="flex flex-col items-center text-center sm:py-10">
            <div className="flex items-center justify-center rounded-full bg-[#C6F9E1] mb-6 p-8 sm:p-10">
              <div className="sm:w-[120px] sm:h-[120px] w-[80px] h-[80px] flex items-center justify-center text-emerald-500 md:w-20 md:h-20">
                <TickIconCircleFill className="w-full h-full" />
              </div>
            </div>
            <h1 className="text-3xl font-semibold text-gray-800 mb-3 md:text-2xl">
              Assessment Submitted
            </h1>
            <p className="text-base text-gray-500">
              Your assessment has been successfully submitted. Thank you.
            </p>
          </div>

          {/* Feedback Section */}
          <div className="bg-white rounded-xl overflow-hidden shadow-sm">
            {/* Feedback Header */}
            <div className="bg-[#344054] py-5 px-6 sm:px-8">
              <h2 className="text-lg sm:text-xl font-semibold text-white m-0">Help Us Improve</h2>
            </div>

            {/* Feedback Content */}
            <div className="p-6 sm:p-8">
              {/* Rating Container */}
              <div className="mb-8">
                <p className="text-center text-base font-semibold text-[#101828] mb-6">
                  How was your Assessment Experience?
                </p>
                <div className="flex items-start justify-center gap-3 sm:gap-8">
                  {RATING_OPTIONS.map((option) => {
                    const isSelected = rating === option.value;
                    const isDimmed = rating !== null && !isSelected;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleRatingClick(option.value)}
                        aria-label={option.label}
                        aria-pressed={isSelected}
                        className="flex flex-col items-center gap-2 p-0 !m-0 bg-transparent border-none cursor-pointer transition-transform duration-200 hover:scale-110 focus:outline-none"
                      >
                        <span className="relative">
                          <span
                            className={`block text-3xl sm:text-4xl leading-none transition-all duration-200 ${isDimmed ? 'opacity-50' : ''
                              }`}
                          >
                            {option.emoji}
                          </span>
                          {isSelected && (
                            <span className="absolute top-0 -right-2 flex items-center justify-center">
                              <svg
                                className="w-2.5 h-auto"
                                viewBox="0 0 13 11"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M0.75 6.6875C0.75 6.6875 1.9375 6.6875 3.52083 9.45833C3.52083 9.45833 7.92171 2.20112 11.8333 0.75"
                                  stroke="#069952"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                          )}
                        </span>
                        <span
                          className={`text-xs sm:text-sm transition-colors duration-200 ${isSelected ? 'text-[#344054]' : 'text-gray-400'
                            }`}
                        >
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Comment Container */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-[#101828] mb-1">
                  Tell us what worked well or what could be improved.
                </label>
                <p className="text-sm text-gray-500 mb-3 leading-relaxed">
                  We thrive on candor we'd like to see this in the potential candidates we're considering. Good or bad share with us.
                </p>
                <textarea
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-[#344054] resize-vertical transition-colors duration-200 focus:outline-none focus:border-[#2E5CFF] placeholder:text-gray-400"
                  placeholder="Tell us..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={6}
                />
              </div>

              {/* Submit Button Container */}
              <div className="flex justify-end">
                <button
                  onClick={handleSubmitFeedback}
                  disabled={isSubmittingFeedback || isSubmitted}
                  className="px-6 py-3 bg-[#012169] text-white rounded-md border-none font-medium transition-opacity disabled:opacity-60"
                >
                  {isSubmittingFeedback ? 'Submitting...' : isSubmitted ? 'Submitted' : 'Submit'}
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-center gap-2 py-6">
            <span className="text-sm text-gray-500">Enabled by</span>
            <LogoWithText className='' />
          </div>
        </div>
      </div>
    </div>
  );
};

export { AssessmentFeedback };
